import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createMarketplaceIntegrationApiV1OrganizationsOrgIdMarketplaceIntegrationsPost,
    getMarketplaceIntegrationsApiV1OrganizationsOrgIdMarketplaceIntegrationsGet,
    updateMarketplaceIntegrationApiV1OrganizationsOrgIdMarketplaceIntegrationsIntegrationIdPut,
} from '@/common/api/client/sdk.gen'
import type {
    MarketplaceIntegrationCreate,
    MarketplaceIntegrationResponse,
    MarketplaceIntegrationUpdate,
} from '@/common/api/client/types.gen'
import { client } from '@/common/lib/fetch'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import { useEffect } from 'react'

import { useCurrentOrg } from '../../org/model'
import { AxiosError } from 'axios'

const getIntegrations = async (orgId: string) => {
    const res = await getMarketplaceIntegrationsApiV1OrganizationsOrgIdMarketplaceIntegrationsGet({
        path: { org_id: orgId },
    })
    return res.data!
}

const createIntegration = async (orgId: string, data: MarketplaceIntegrationCreate) => {
    const res = await createMarketplaceIntegrationApiV1OrganizationsOrgIdMarketplaceIntegrationsPost({
        path: { org_id: orgId },
        body: data,
    })
    return res.data!
}

const updateIntegration = async (orgId: string, integrationId: string, data: MarketplaceIntegrationUpdate) => {
    const res = await updateMarketplaceIntegrationApiV1OrganizationsOrgIdMarketplaceIntegrationsIntegrationIdPut({
        path: { org_id: orgId, integration_id: integrationId },
        body: data,
    })
    return res.data!
}

/** В OpenAPI/SDK нет операции DELETE — тот же URL, что и раньше в api-слое */
const deleteIntegration = async (orgId: string, integrationId: string): Promise<void> => {
    await client.delete(`/api/v1/organizations/${orgId}/marketplace-integrations/${integrationId}`)
}

export const useOrgIntegrations = (org_id?: string) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['org_integrations', org_id],
        queryFn: () => getIntegrations(org_id as string),
        enabled: !!org_id,
    })
    return { data, isLoading, error }
}

export const useIntegrationsForm = (editingIntegration?: MarketplaceIntegrationResponse) => {
    const { data: currentOrg } = useCurrentOrg()
    const org_id = currentOrg?.id
    const isEditMode = !!editingIntegration

    const queryClient = useQueryClient()
    const {
        form,
        handleSubmit,
        onSubmit: originalOnSubmit,
        register,
        isLoading,
        isError,
        isSuccess,
        error,
    } = useFormMutation<
        MarketplaceIntegrationCreate,
        MarketplaceIntegrationResponse,
        {
            detail?: string
            field_errors?: Record<keyof MarketplaceIntegrationCreate, string>
        }
    >({
        mutationFn: data => {
            const processedData = { ...data }

            if (isEditMode && editingIntegration) {
                return updateIntegration(
                    org_id as string,
                    editingIntegration.id,
                    processedData as MarketplaceIntegrationUpdate
                )
            }
            return createIntegration(org_id as string, processedData as MarketplaceIntegrationCreate)
        },
        toastOptions: {
            successMessage: () => (isEditMode ? 'Интеграция обновлена' : 'Интеграция создана'),
        },
        onSuccess: result => {
            if (isEditMode) {
                queryClient.setQueryData(
                    ['org_integrations', org_id],
                    (old: MarketplaceIntegrationResponse[]) =>
                        old?.map(integration =>
                            integration.id === editingIntegration?.id ? { ...integration, ...result } : integration
                        ) || []
                )
            } else {
                queryClient.setQueryData(['org_integrations', org_id], (old: MarketplaceIntegrationResponse[]) => [
                    ...(old || []),
                    result,
                ])
            }
            queryClient.invalidateQueries({ queryKey: ['org_integrations'] })
            return result
        },
    })

    const selectedMarketplace = form.watch('marketplace')

    useEffect(() => {
        if (isEditMode && editingIntegration) {
            form.reset({
                marketplace: editingIntegration.marketplace,
                api_token: editingIntegration.token_preview || '',
                client_id: editingIntegration.client_id_preview || '',
            })
        } else {
            form.reset({
                marketplace: undefined,
                api_token: '',
                client_id: '',
            })
        }
    }, [form, isEditMode, editingIntegration])

    useEffect(() => {
        if (selectedMarketplace && selectedMarketplace !== 'ozon') {
            form.setValue('client_id', '')
        }
    }, [selectedMarketplace, form])

    const shouldShowClientId = selectedMarketplace === 'ozon'

    return {
        form,
        handleSubmit,
        onSubmit: originalOnSubmit,
        register,
        isPending: isLoading,
        isError,
        isSuccess,
        error,
        shouldShowClientId,
        isEditMode,
    }
}

export { useMarketplaceOauthIntegration } from './useMarketplaceOauthIntegration'

export const useRemoveIntegration = () => {
    const { data: currentOrg } = useCurrentOrg()
    const org_id = currentOrg?.id

    const queryClient = useQueryClient()
    return useMutation<void, AxiosError, string>({
        mutationFn: integration_id => deleteIntegration(org_id as string, integration_id),
        onSuccess: (_, integration_id) => {
            queryClient.removeQueries({ queryKey: ['org_integrations', integration_id] })
            queryClient.invalidateQueries({ queryKey: ['org_integrations'] })
        },
    })
}
